import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { routes } from "router"
import { getBooking, cleanBooking } from "store/bookings"
import { cleanTrip, receivedTrip } from "store/trips"
import { TripsPreview } from "components/trips/TripsPreview"
import Spinner from "components/common/Spinner"
import { useTheme } from "context/AntdThemeContext"
import { useTitle } from "hooks/useTitle"
import { showOnHostedBookingPage } from "helpers/embedded"
import OrderBookingTabs from "../OrderBookingTabs"
import BookingSuccess from "../WidgetCheckout/BookingSuccess"

// A paid booking at its own address (/widget/:uuid/booking/:id; a cart order adds ?bookings=<uuid>,…
// for a tab per booking), so a refresh or the link later shows it (QA Ticketing #27). Checkout lands
// here with the success popup on top (navigation state, so a refresh shows only the booking). On our
// hosted /book page the address bar follows, as /book/:uuid/booking/:id.
const WidgetBooking = () => {
  const { uuid, id } = useParams()
  const { search, state } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { switchTheme } = useTheme()
  const { booking } = useSelector((store) => store.bookings)
  const { trip, loading } = booking
  const bookingsParam = new URLSearchParams(search).get("bookings")
  const bookingUuids = useMemo(() => (bookingsParam ? bookingsParam.split(",").filter(Boolean) : []), [bookingsParam])
  const adventureMode = trip?.guide?.adventure_mode
  useTitle(trip?.title)

  useEffect(() => {
    dispatch(getBooking(id, true)).then(({ trip: bookedTrip }) => dispatch(receivedTrip(bookedTrip)))
    return () => dispatch(cleanBooking()) && dispatch(cleanTrip())
  }, [id, dispatch])

  useEffect(() => {
    if (adventureMode) switchTheme(adventureMode)
  }, [adventureMode, switchTheme])

  useEffect(() => {
    showOnHostedBookingPage(`${routes.hostedBookingDetail({ uuid, id })}${search}`)
  }, [uuid, id, search])

  const openBooking = (bookingUuid) => navigate({ pathname: routes.widgetBooking({ uuid, id: bookingUuid }), search }, { replace: true })
  const closeSuccess = () => navigate({ pathname: routes.widgetBooking({ uuid, id }), search }, { replace: true, state: null })
  const bookAnother = () => {
    showOnHostedBookingPage(routes.hostedBookingListing({ uuid }))
    navigate(routes.widgetListing({ uuid }))
  }

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end">
        <Button shape="round" color="primary" variant="outlined" onClick={bookAnother}>
          {t("booking_widget.success.book_another")}
        </Button>
      </Flex>
      {bookingUuids.length > 1 && <OrderBookingTabs bookingUuids={bookingUuids} bookingUuid={id} onSelect={openBooking} />}
      {loading ? (
        <Flex justify="center" className="py-15">
          <Spinner />
        </Flex>
      ) : (
        booking.id && <TripsPreview />
      )}
      {state?.bookedOrder && <BookingSuccess order={state.bookedOrder} onClose={closeSuccess} />}
    </Flex>
  )
}

export default WidgetBooking
